import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { Link } from 'Components/molecules/Link';
import { Tabs } from 'Components/molecules/Tabs';
import { withPolaris } from 'Components/protons/Polaris';
import { getArticleGroup } from 'SharedPartials/getArticleGroup';

import './_style.scss';

/**
 * FiltersBlock component
 *
 * This component renders a block of filters and articles.
 *
 * @param {Object} props - The props object
 * @param {Object} props.articleCardProps - Props to be passed to the article card component
 * @param {string} props.articleCardStyle - Style for the article card
 * @param {number} props.articlesPerRow - Number of articles to display per row
 * @param {Array} props.content - Array of content objects
 * @param {Object} props.context - Context object containing additional information
 * @param {string} props.context.cssPrefix - CSS prefix for class names
 * @param {string} props.ctaLabel - Label for the call-to-action link
 * @param {string} props.ctaUrl - URL for the call-to-action link
 * @param {Object} props.extraClassNames - Additional class names to be applied
 * @param {boolean} props.loadStyles - Whether to apply the class names for styling
 * @param {string} props.title - Title of the filters block
 */
const FiltersBlock = ({
  articleCardProps,
  articleCardStyle,
  articlesPerRow,
  childrenContent,
  childrenContentLimit,
  content,
  contentLimit,
  context: { cssPrefix },
  ctaLabel,
  ctaUrl,
  extraClassNames,
  loadStyles,
  title,
}) => {
  // modify the content array to set the image in the first item to use hero transformation
  const modifiedContent = content.map((item, index) => ({
    ...item,
    imageSize: index === 0 ? 'hero' : item.imageSize,
  })).slice(0, contentLimit); // limit the number of articles to 5;

  // modify the childrenContent array to set the image in the first item to use hero transformation
  const modifiedChildrenContent = childrenContent.map((child) => ({
    ...child,
    content: child.content.map((item, index) => ({
      ...item,
      imageSize: index === 0 ? 'hero' : item.imageSize,
    })).slice(0, childrenContentLimit), // limit the number of articles to 5;
  }));

  // create an array of objects with title and content
  // each object will be a tab
  const contentArray = [
    {
      title: 'All',
      content: getArticleGroup({
        articleCardProps,
        articleCardStyle,
        articlesPerRow,
        content: modifiedContent,
        extraClassNames,
        title: '', // pass empty title to fulfill the condition in getArticleGroup
      }),
    },
    ...modifiedChildrenContent.map((child) => ({
      title: child.title,
      content: getArticleGroup({
        articleCardProps,
        articleCardStyle,
        articlesPerRow,
        content: child.content,
        extraClassNames,
        title: '', // pass empty title to fulfill the condition in getArticleGroup
        more: {
          label: child.ctaLabel,
          url: child.ctaUrl,
        },
      }),
    }
    )),
  ];

  return (
    <div className={classNames({
      [`${cssPrefix}__filters-block`]: loadStyles,
      ...extraClassNames,
    })}
    >
      {title && (
        <div className={`${cssPrefix}__filters-block--header ${cssPrefix}__article-group--header`}>
            {title && (
              <div className={`${cssPrefix}__article-group--title`}>
                {title}
              </div>
            )}

          {ctaUrl && (
            <div className={`${cssPrefix}__article-group--cta`}>
              <Link href={ctaUrl}>{ctaLabel}</Link>
            </div>
          )}
        </div>
      )}
      <div className={`${cssPrefix}__filters-block--main`}>
        <Tabs
          tabs={contentArray}
          visibleTabIndex={0}
          showTitle={false}
          extraClassNames={{
            [`${cssPrefix}__filters-block--tabs`]: true,
          }}
        />
      </div>
    </div>
  );
};

FiltersBlock.propTypes = {
  articleCardProps: PropTypes.shape(),
  articleCardStyle: PropTypes.string,
  articlesPerRow: PropTypes.number,
  childrenContent: PropTypes.arrayOf(PropTypes.shape),
  childrenContentLimit: PropTypes.number,
  content: PropTypes.arrayOf(PropTypes.shape),
  contentLimit: PropTypes.number,
  context: PropTypes.shape({
    cssPrefix: PropTypes.string.isRequired,
  }).isRequired,
  ctaLabel: PropTypes.string,
  ctaUrl: PropTypes.string,
  extraClassNames: PropTypes.shape(),
  loadStyles: PropTypes.bool,
  title: PropTypes.string,
};

FiltersBlock.defaultProps = {
  articleCardProps: {},
  articleCardStyle: '',
  articlesPerRow: 3,
  childrenContent: [],
  childrenContentLimit: 5,
  content: [],
  contentLimit: 5,
  ctaLabel: '',
  ctaUrl: '',
  extraClassNames: {},
  loadStyles: true,
  title: '',
};

export default withPolaris(FiltersBlock);
